/** Design Demo drawer for stationery; same sticky-footer layout as DesignPreviewContainer. */
'use client'

import { useCallback } from 'react'
import { SideDrawer } from '@/components/ui/Drawer'
import StationeryPreview from '@/components/StationeryPreview'
import { Button } from '@/components/ui/Button'
import type { StationeryDesign } from './StationeryDesignCard'

interface StationeryDesignPreviewContainerProps {
  isOpen: boolean
  onClose: () => void
  design: StationeryDesign | null
  onSelect: (design: StationeryDesign) => void
}

export default function StationeryDesignPreviewContainer({
  isOpen,
  onClose,
  design,
  onSelect,
}: StationeryDesignPreviewContainerProps) {
  const handleSelect = useCallback(() => {
    if (!design) return
    onClose()
    onSelect(design)
  }, [design, onClose, onSelect])

  if (!design) return null

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Design Demo"
      contentLayout="sticky-footer"
    >
      {isOpen && (
        <div className="flex flex-col h-full min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scroll-smooth overscroll-contain scrollbar-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className="py-3 px-2 text-center border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                {design.label}
              </h3>
              {design.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {design.description}
                </p>
              )}
            </div>

            <div className="p-4">
              <StationeryPreview templateData={design.data} maxWidth={380} />
            </div>
          </div>
          <div className="shrink-0 p-4 border-t border-border bg-card">
            <Button
              onClick={handleSelect}
              className="w-full touch-manipulation"
              size="lg"
            >
              Use This Design
            </Button>
          </div>
        </div>
      )}
    </SideDrawer>
  )
}
